// =================================================================================================
// >>> **THE ASTROLOGY READING — THE LAYOUT SHE APPROVED, AS THE ONE RENDERER.** <<<
//
// Her order, 2026-09-06/07: *"the exact format/design of the approved artifact, live in the room"*,
// and her amendment: *"One shared reading-presentation-and-voice standard so no astrology reading
// anywhere reverts to generic astrology-speak or the old layout."*
//
// ---------------------------------------------------------------------------------------------
// >>> **IT IS DISPATCHED ON THE SHAPE OF THE DATA, NOT ON A LIST OF TYPES.** <<< `ReportBody` sends
// anything carrying `sections` here. That is what makes a future reading inherit the layout by
// existing rather than by somebody remembering to add a line — the same reason
// `src/astroReadingStandard.js` rosters the EXCEPTIONS instead of the members.
//
// >>> **AND IT DOES NOT REPLACE THE OLD VIEW, IT SITS BESIDE IT.** <<< There are 3,374 birth charts
// already saved in the old shape and those rows are members' own readings. A renderer that could
// only draw the new shape would blank every one of them, which is the defect this house has spent
// two days on from the other direction. Old rows keep `ReportView`; new ones come here.
//
// >>> **THE TECHNICAL LINE SITS AT THE FOOT OF EACH SECTION AND NOWHERE ELSE.** <<< Her own words:
// *"we're telling them already at the bottom that you have this placement in this house, so we don't
// need to explain that in the top."* The placement is ON the page. It is simply not in the prose,
// which is what the writing law enforces and this component makes true of the layout.
// =================================================================================================
import menuLogoUrl from "../assets/menu-logo.webp";

export type AstroFrontispieceRow = { label: string; value: string };

export type AstroSection = {
  key: string;
  title: string;
  body: string;
  /** The placement this section rests on. Drawn at the FOOT, never woven into the words. */
  coord?: string;
  /** Her clause 5's private bodies — drawn as their own panel rather than in the plain run. */
  deep?: boolean;
};

export type AstroReadingData = {
  kicker?: string;
  frontispiece?: AstroFrontispieceRow[];
  overview?: string;
  sections?: AstroSection[];
  /** Named, never silently absent: the five that wait on `seas_18.se1`. */
  awaiting?: string[];
};

/** Blank lines are paragraph breaks. A single `\n` inside a paragraph is not one. */
function paragraphs(text: string): string[] {
  return String(text || "").split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);
}

export function AstroReadingView({ data }: { data: AstroReadingData }) {
  const front = Array.isArray(data.frontispiece) ? data.frontispiece.filter((r) => r && r.value) : [];
  const sections = Array.isArray(data.sections) ? data.sections.filter((s) => s && s.body) : [];
  const overview = paragraphs(data.overview || "");

  return (
    <article className="astro-reading">
      {/* HER EYE, 2026-09-06: the logo is REDUCED on a reading. It is a reading, not a billboard,
          and the name at the top should be the member's rather than the house's. */}
      <header className="astro-reading__band">
        <img src={menuLogoUrl} alt="12th House" className="astro-reading__logo" />
        {data.kicker ? <p className="astro-reading__kicker">{data.kicker}</p> : null}
      </header>

      {/* >>> HER APPROVAL CONDITION: the details SHE gave us, at the top, so the reading is visibly
          hers before she has read a word. It is the one place the technical facts are welcome,
          because they are her own answers rather than the machinery. <<< */}
      {front.length ? (
        <dl className="astro-reading__front">
          {front.map((row) => (
            <div key={row.label}>
              <dt>{row.label}</dt>
              <dd>{row.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}

      {overview.length ? (
        <section className="astro-reading__overview">
          {overview.map((para, i) => <p key={i}>{para}</p>)}
        </section>
      ) : null}

      {sections.length ? (
        <div className="astro-reading__sections">
          {sections.map((s) => (
            <article key={s.key} className={s.deep ? "astro-reading__card astro-reading__card--deep" : "astro-reading__card"}>
              <h3>{s.title}</h3>
              {paragraphs(s.body).map((para, i) => <p key={i}>{para}</p>)}
              {/* The placement, at the foot. On the page, and out of the writing. */}
              {s.coord ? <p className="astro-reading__coord">{s.coord}</p> : null}
            </article>
          ))}
        </div>
      ) : null}
    </article>
  );
}
